import { AlertType } from '@/models';
import { InteractionType, LevelData } from '@/api';
import {
  alertGradePriority,
  alertGrades,
} from './TransactionAlertsEditModal.constants';

const findClosestNext = (
  levels: LevelData,
  alertGrade: AlertType,
): { grade: AlertType; value: number } | null => {
  const currentPriority = alertGradePriority[alertGrade];
  const grade = alertGrades.find(
    (g) => alertGradePriority[g] > currentPriority && levels[g] != null,
  );

  return grade ? { grade, value: levels[grade]! } : null;
};

const findClosestPrev = (
  levels: LevelData,
  alertGrade: AlertType,
): { grade: AlertType; value: number } | null => {
  const currentPriority = alertGradePriority[alertGrade];
  const reversed = [...alertGrades].reverse();
  const grade = reversed.find(
    (g) => alertGradePriority[g] < currentPriority && levels[g] != null,
  );

  return grade ? { grade, value: levels[grade]! } : null;
};

const validateLevel = (
  value: number | null,
  levels: LevelData,
  alertGrade: AlertType,
  type: InteractionType,
): string | null => {
  if (value == null) return null;
  const next = findClosestNext(levels, alertGrade);
  const prev = findClosestPrev(levels, alertGrade);

  if (next && value >= next.value) {
    return `The ${type} value cannot be greater than the closest next level (${next.grade}: ${next.value}).`;
  }

  if (prev && value <= prev.value) {
    return `The ${type} value cannot be less than the closest previous level (${prev.grade}: ${prev.value}).`;
  }

  return null;
};

interface ValidateAlertLevelsParams {
  alertGrade: AlertType;
  direct: number | null;
  indirect: number | null;
  directLevels: LevelData;
  indirectLevels: LevelData;
}

export const validateAlertLevels = ({
  alertGrade,
  direct,
  indirect,
  directLevels,
  indirectLevels,
}: ValidateAlertLevelsParams): { valid: boolean; error: string | null } => {
  const directError = validateLevel(direct, directLevels, alertGrade, 'direct');

  if (directError) {
    return { valid: false, error: directError };
  }

  const indirectError = validateLevel(
    indirect,
    indirectLevels,
    alertGrade,
    'indirect',
  );

  if (indirectError) {
    return { valid: false, error: indirectError };
  }

  return { valid: true, error: null };
};
